<template>
  <div class="denuvo-page">
    <t-card class="denuvo-panel form-panel" :bordered="false">
      <template #title>提取信息</template>
      <t-form :data="form" label-align="left" :label-width="96" @submit="startExtract">
        <div class="form-grid">
          <t-form-item label="授权用户" name="dbUsername">
            <t-input v-model="form.dbUsername" clearable placeholder="被授权 GamePlanet 游戏星球账号" />
          </t-form-item>
          <t-form-item label="游戏 AppID" name="appid">
            <t-input v-model="form.appid" clearable placeholder="请输入 AppID" />
          </t-form-item>
          <t-form-item label="Steam 账号" name="accountName">
            <t-input v-model="form.accountName" clearable placeholder="拥有对应 AppID 权限的 STEAM 账号" />
          </t-form-item>
          <t-form-item label="Steam 密码" name="password">
            <t-input v-model="form.password" type="password" clearable placeholder="STEAM 登录密码" />
          </t-form-item>
          <t-form-item label="Steam 验证码" name="twoFactorCode" class="span-2">
            <t-input v-model="form.twoFactorCode" clearable placeholder="可留空，需要时会弹窗问询" />
          </t-form-item>
        </div>
        <div class="form-actions">
          <t-button theme="primary" :loading="running" type="submit">开始提取并上传</t-button>
          <t-button variant="outline" :disabled="running" @click="clearResult">清空结果</t-button>
        </div>
      </t-form>
    </t-card>

    <section class="denuvo-grid">
      <t-card v-for="item in statCards" :key="item.key" class="metric-card" :bordered="false">
        <div class="metric-card__label">{{ item.label }}</div>
        <div class="metric-card__value" :title="String(item.value)">{{ item.value }}</div>
      </t-card>
    </section>

    <t-card class="denuvo-panel result-panel" :bordered="false">
      <template #title>执行结果</template>
      <t-loading :loading="running" text="任务运行中">
        <div class="result-box">{{ summaryText }}</div>
      </t-loading>
    </t-card>

    <t-dialog
      v-model:visible="inputDialog.visible"
      :header="inputDialog.title"
      confirm-btn="提交"
      cancel-btn="取消"
      :close-on-overlay-click="false"
      @confirm="submitTaskInput"
    >
      <p class="dialog-message">{{ inputDialog.message }}</p>
      <t-input
        v-model="inputDialog.value"
        :type="inputDialog.kind === 'password' ? 'password' : 'text'"
        autofocus
        clearable
        placeholder="请输入后继续"
        @enter="submitTaskInput"
      />
    </t-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { MessagePlugin } from 'tdesign-vue-next';
import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import { useStore } from 'vuex';

const NODE_API_BASE = 'https://api.gameplanet.vip';

export default {
  name: 'DenuvoTicketUpload',
  setup() {
    const store = useStore();
    const form = reactive({ dbUsername: '', appid: '', accountName: '', password: '', twoFactorCode: '' });
    const running = ref(false);
    const taskId = ref('');
    const result = ref(null);
    let pollTimer = null;

    const inputDialog = reactive({ visible: false, kind: '', title: '需要输入', message: '', value: '' });
    const resultRecord = computed(() => result.value?.record || {});
    const shortSteamId = computed(() => {
      const value = String(resultRecord.value.steamID64 || '');
      return value.length > 12 ? `${value.slice(0, 11)}...` : value || '-';
    });
    const statCards = computed(() => [
      { key: 'appid', label: 'AppID', value: resultRecord.value.appid || form.appid || '-' },
      { key: 'user', label: '授权用户', value: resultRecord.value.authorizedUsername || form.dbUsername || '-' },
      { key: 'steam', label: 'SteamID64', value: shortSteamId.value },
    ]);
    const summaryText = computed(() => {
      const payload = result.value;
      if (!payload) return '等待任务...';
      if (payload.status?.startsWith?.('awaiting_')) return `等待输入：${payload.awaiting?.message || '需要继续验证'}`;
      if (payload.status === 'error' || payload.ok === false) return payload.error || JSON.stringify(payload, null, 2);
      if (!payload.ok) return JSON.stringify(payload, null, 2);
      const record = payload.record || {};
      return [
        '完成', '',
        `AppID: ${record.appid || '-'}`,
        `授权用户: ${record.authorizedUsername || form.dbUsername || '-'}`,
        `SteamID64: ${record.steamID64 || '-'}`,
        `CM: ${record.cmServer?.endpoint || '-'}${record.cmServer?.latency ? ` (${record.cmServer.latency}ms)` : ''}`,
        '', '已上传接口:', '- saveETicket', '- saveGameTicket', '', '票证大小:',
        `Data: ${record.sizes?.Data_AppOwnershipTicket_bytes || 0} bytes`,
        `EData: ${record.sizes?.EData_EncryptedAppTicket_bytes || 0} bytes`,
      ].join('\n');
    });

    const api = axios.create({ baseURL: NODE_API_BASE, timeout: 120000, headers: { 'Content-Type': 'application/json' } });
    const adminUserHeader = () => String(store.state.user.username || 'GamePlanetAdmin');
    const validate = () => {
      if (!form.dbUsername.trim()) return '请填写授权用户。';
      const appid = Number(form.appid);
      if (!Number.isInteger(appid) || appid <= 0) return '请填写正确的游戏 AppID。';
      if (!form.accountName.trim()) return '请填写 Steam 账号。';
      return '';
    };
    const clearPoll = () => { if (pollTimer) window.clearInterval(pollTimer); pollTimer = null; };
    const normalizeTask = (data) => {
      result.value = data;
      if (data.awaiting) {
        inputDialog.kind = data.awaiting.kind || '';
        inputDialog.title = data.awaiting.kind === 'password' ? '需要 Steam 密码' : '需要 Steam 验证码';
        inputDialog.message = data.awaiting.message || '请输入后继续';
        inputDialog.value = '';
        inputDialog.visible = true;
      }
    };
    const pollTask = async () => {
      if (!taskId.value) return;
      try {
        const { data } = await api.get(`/api/extract/task/${taskId.value}`, { headers: { 'X-Admin-User': adminUserHeader() } });
        normalizeTask(data);
        if (data.status === 'done') { running.value = false; clearPoll(); MessagePlugin.success('提取并上传完成'); }
        if (data.status === 'error') { running.value = false; clearPoll(); MessagePlugin.error(data.error || '任务失败'); }
      } catch (error) {
        MessagePlugin.error(error.response?.data?.error || error.message || '查询任务失败');
      }
    };
    const startExtract = async () => {
      const msg = validate();
      if (msg) return MessagePlugin.warning(msg);
      clearPoll(); running.value = true; result.value = null; taskId.value = '';
      try {
        const { data } = await api.post('/api/extract/start', {
          ticketMode: 'denuvo', appid: Number(form.appid), dbUsername: form.dbUsername.trim(),
          authorizedUsername: form.dbUsername.trim(), accountName: form.accountName.trim(),
          password: form.password, twoFactorCode: form.twoFactorCode.trim(),
        }, { headers: { 'X-Admin-User': adminUserHeader() } });
        taskId.value = data.taskId;
        normalizeTask(data);
        pollTimer = window.setInterval(pollTask, 1200);
        await pollTask();
      } catch (error) {
        running.value = false;
        MessagePlugin.error(error.response?.data?.error || error.message || '启动失败');
      }
    };
    const submitTaskInput = async () => {
      if (!inputDialog.value.trim()) return MessagePlugin.warning('请输入内容后再提交');
      try {
        inputDialog.visible = false;
        const { data } = await api.post(`/api/extract/task/${taskId.value}/input`, { value: inputDialog.value.trim() }, { headers: { 'X-Admin-User': adminUserHeader() } });
        normalizeTask(data);
        await pollTask();
      } catch (error) {
        MessagePlugin.error(error.response?.data?.error || error.message || '提交失败');
      }
    };
    const clearResult = () => { result.value = null; taskId.value = ''; clearPoll(); running.value = false; };
    onBeforeUnmount(clearPoll);
    return { clearResult, form, inputDialog, running, startExtract, statCards, submitTaskInput, summaryText };
  },
};
</script>

<style scoped>
.denuvo-page { display: grid; gap: 18px; }
.denuvo-panel, .metric-card { border: 1px solid #e7edf5; border-radius: 8px; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04); }
.form-panel { background: #fff; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(280px, 1fr)); column-gap: 24px; }
.form-grid .span-2 { grid-column: span 2; max-width: calc(50% - 12px); }
.form-actions { display: flex; justify-content: flex-end; gap: 12px; padding-top: 4px; }
.denuvo-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.metric-card { min-height: 96px; }
.metric-card__label { margin-bottom: 14px; color: #667085; font-size: 12px; }
.metric-card__value { color: #111827; font-size: 20px; font-weight: 400; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.result-panel { background: #fff; }
.result-box { min-height: 420px; max-height: 620px; overflow: auto; white-space: pre-wrap; word-break: break-all; background: #f8fafc; border: 1px solid #e7edf5; border-radius: 8px; padding: 20px; line-height: 1.7; color: #344054; font-size: 13px; }
.dialog-message { margin: 0 0 12px; color: #667085; }
@media (max-width: 1180px) { .denuvo-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 900px) { .form-grid { grid-template-columns: 1fr; } .form-grid .span-2 { grid-column: auto; max-width: none; } .form-actions { justify-content: flex-start; flex-wrap: wrap; } }
@media (max-width: 560px) { .denuvo-grid { grid-template-columns: 1fr; } }
</style>



